
/**
 * Event configurations: 
 * - carContext - If true car context object is injected to analytic event, look more for getCarContextData() function and ga4.js.
 */
export const EVENTS = { 
  car_reserved: { carContext: true },
  chat_interaction: { carContext: true },
  complaint_form_submit: { carContext: true },
  contact_form_submit: { carContext: true },
  funding_success: { carContext: true },  
  funding_calculator_done: { carContext: true },
  car_maintenance_booking: { carContext: true },
  page_view: { carContext: true },
  form_submit: { carContext: true },
  car_click_from_search: { carContext: true },
  car_click_from_nosto: { carContext: true },
  car_click_browse_image: { carContext: true },
  car_click_info_tab: { carContext: true },  
  car_reserve_click: { carContext: true },
  car_search_done: { carContext: true },
  request_credit_click: { carContext: true },
  car_tradein_form_submit: { carContext: true },
}

/**
 * List of HTML elements considered to be interactive
 * 
 * There is this HTML -specs https://html.spec.whatwg.org/multipage/interactive-elements.html#interactive-elements
 * but if we have some custom elements we need to think definition more..
 */
const interactiveElements = [
  typeof HTMLLinkElement !== 'undefined' ? HTMLLinkElement : undefined, 
  typeof HTMLButtonElement !== 'undefined' ? HTMLButtonElement : undefined,
  typeof HTMLTextAreaElement !== 'undefined' ? HTMLTextAreaElement : undefined,
  typeof HTMLCanvasElement !== 'undefined' ? HTMLCanvasElement : undefined, 
  typeof HTMLAudioElement !== 'undefined' ? HTMLAudioElement : undefined, 
  typeof HTMLLabelElement !== 'undefined' ? HTMLLabelElement : undefined, 
  typeof HTMLDialogElement !== 'undefined' ? HTMLDialogElement : undefined, 
  typeof HTMLSelectElement !== 'undefined' ? HTMLSelectElement : undefined,
  typeof HTMLInputElement !== 'undefined' ? HTMLInputElement : undefined, 
  typeof HTMLDetailsElement !== 'undefined' ? HTMLDetailsElement : undefined
].filter(Boolean);

/**
 * Check is element checked or selected.
 * 
 * @param {HTMLElement} element element to be 
 * @returns {boolean} true if elemented has selected or checked attribute true.
 */
const isSelectedOrChecked = element => element.value && (element.selected || element.checked);

/**
 * Parse selected values from select
 * 
 * @param {HTMLElement[]} data html elements from form object
 * @param {Function} filterFunction function used to filter selected/checked options
 * @returns {HTMLElement[]} elements array
 */
const stringifyFilterArray = (data, filterFunction ) => {
  if( data instanceof HTMLElement ) {
    return Array.from(data)?.filter( filterFunction )
      ?.map( element => element.value ).join(', ')    
  } else {
    return '';
  }
}

/**
 * Send car search event to data layer
 *
 * @param {FormElement} form element
 * @returns {object} event object
 */
export const formCarSearchEvent = (form) => {

  const event = {
    name: 'car_search_done' ,
    params: {
      'bodytype': stringifyFilterArray(form['body_type[]'], isSelectedOrChecked),
      'cartype': stringifyFilterArray(form['sales_type[]'], isSelectedOrChecked),
      'drivetrain': stringifyFilterArray(form['drive[]'], isSelectedOrChecked),
      'fueltype': stringifyFilterArray(form['fuel[]'], isSelectedOrChecked),
      'geartype': stringifyFilterArray(form['transmission[]'], isSelectedOrChecked),
      'manufacturer': form?.make?.value ?? '',
      'mileage': `Max: ${form?.max_mileage?.value} km`,
      'model': stringifyFilterArray(form['model[]'], isSelectedOrChecked),
      'outlet': stringifyFilterArray(form['location[]'], isSelectedOrChecked),
      'price': `Min: ${form?.min_price?.value || ' - '} € Max: ${form?.max_price?.value || ' - '} €`,
      'registernumber': form?.license?.value ?? '',
    }
  }

  return event;
}

export const isBilia = new RegExp(/^(https|http)(:\/\/bilia\.fi)/);
export const isBiliaAdmin = new RegExp(/^(https|http)(:\/\/bilia-admin\.stage\.crasman\.fi)/);

/**
 * Is url internal URL -address
 * 
 * @param {string?} url url address
 * @returns {boolean} true if url is internal url address false if not
 */
export const isInternalUrl = ( url ) => {
  let isInternalUrl = false;

  if( typeof url === 'string' ) {
    isInternalUrl = isBilia.test(url) || isBiliaAdmin.test(url);
  }
  
  return isInternalUrl;
}

/**
 * Has event car context
 * 
 * @param {string} eventName event name
 * @returns {boolean} true if event has a car context
 */
export const hasEventCarContext = (eventName) => {
  return EVENTS[eventName]?.carContext;
}

/**
 * Try to get car context object from window object.
 * 
 * @returns {object}
 */
export const getCarContextData = () => {
  return window.carContext;
}

/**
 * Is passed object interactive HTMLElement
 * 
 * @param {*} element element to be checked for interactivity :P
 * @returns {boolean} true if element is considered to be interactive element false otherwise
 */
export const isInterActiveElement = (element) => {
  return element && interactiveElements.some( type => element instanceof type );
}

/**
 * Return interactive element's related text
 * 
 * @param {HTMLElement?} element mouse click target element
 * @return {string} text
 */
export const getInterActiveElementText = (element) => {
  
  let text = '';

  if( element instanceof HTMLElement ) {
    if( element.id ) {
      const elementLabel = document.querySelector(`[for="${element.id}"]`);
  
      if( elementLabel ) {
        text = elementLabel.innerText;
      }    
    } else if( element instanceof HTMLInputElement && element.type === 'submit' ) {
      text = element.value;
    } else {
      text = element.innerText;
    }     
  } 

  return text;
}